import { client, gql } from 'data-provider';
import { IAvatar, ISort } from 'interfaces';
import { WarehouseQuantity } from 'utilities/warehouse-quantity-columns';

export interface IInventoryLots {
  id: string;
  quantity: number;
  unitPrice: number;
  lotEntryDate: Date;
}

export interface InventoryLotWithVariant {
  id: string;
  quantity: number;
  unitPrice: number;
  subTotal: number;
  lotEntryDate: string;
  productVariant: {
    id: string;
    name: string;
    sku: string;
    photo?: IAvatar;
  };
}

export interface InventoriesQueryParams {
  warehouse?: string;
  searchString?: string;
  filterOptions?: { field: string; value: string }[];
  productType?: string;
  start?: number;
  limit?: number;
  sort?: ISort | null;
  includeWarehouseQuantities?: boolean;
}

export interface LowStockInventoriesQueryParams {
  start?: number;
  limit?: number;
}

export interface IInventoryLotResponse {
  id?: string | null;
  productVariant: {
    id: string;
    sku: string;
    name: string;
    type: string;
    avatar: string;
    productTags: string;
    productCategories: string;
    purchasePrice: number;
    sellingPrice: number;
  };
  warehouseName?: string | null;
  minUnit?: number | null;
  quantity: number;
  unitPrice: number;
  totalStockValue: number;
  warehouseQuantities?: WarehouseQuantity[] | null;
}

// Asked for only when inventory.allShowroomColumns is on: an API without the field would reject
// the whole query.
const warehouseQuantitiesSelection = (includeWarehouseQuantities?: boolean) =>
  includeWarehouseQuantities ? 'warehouseQuantities { warehouseId warehouseName quantity }' : '';

export const getInventoryLot = async (warehouseId: string, productId: string) => {
  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      inventoryLots(sort: $sort, where: $where, start: $start, limit: $limit) {
        id
        quantity
        unitPrice
        lotEntryDate
      }
    }
  `;

  interface Data {
    inventoryLots: IInventoryLots[];
  }
  const variables = { where: { product: productId, warehouse: warehouseId }, start: 0, limit: 10 };
  const data: Data = await client.request(query, variables);
  return data.inventoryLots;
};

export const getInventoryLotsByVariant = async (productVariantId: string, warehouseId?: string) => {
  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      inventoryLotsCopy(sort: $sort, where: $where, start: $start, limit: $limit) {
        id
        quantity
        unitPrice
        subTotal
        lotEntryDate
        productVariant {
          id
          name
          sku
        }
      }
    }
  `;

  const variables = {
    where: {
      productVariant: productVariantId,
      warehouse: warehouseId,
    },
  };
  const data = await client.request(query, variables);
  return data.inventoryLotsCopy as InventoryLotWithVariant[];
};

export const getQuantityOfProduct = async (warehouseId: string, productId: string) => {
  const query = gql`
    query ($warehouseId: ID!, $productId: ID!) {
      inventoryByProductAndWarehouse(warehouseId: $warehouseId, productId: $productId) {
        quantity
      }
    }
  `;

  interface Data {
    inventoryByProductAndWarehouse: {
      quantity: number;
    };
  }
  const variables = { warehouseId, productId };
  const data: Data = await client.request(query, variables);
  return data.inventoryByProductAndWarehouse.quantity;
};

export const getVariantLotsByWarehouse = async (warehouseId: string) => {
  const query = gql`
    query ($where: JSON) {
      getVariantLotsByWarehouse(where: $where) {
        id
        lotEntryDate
        quantity
        subTotal
        unitPrice
        productVariant {
          id
          name
          sku
          photo {
            name
            url
            thumbUrl
            size
            uid
          }
        }
      }
    }
  `;

  const variables = {
    where: { warehouse: warehouseId },
  };
  const data = await client.request(query, variables);
  return data.getVariantLotsByWarehouse as InventoryLotWithVariant[];
};

export const getVariantLotsByWarehouseAndVariantIds = async (
  warehouseId: string,
  variantIds: string[] = [],
) => {
  const query = gql`
    query ($where: JSON) {
      getVariantLotsByWarehouse(where: $where) {
        id
        lotEntryDate
        quantity
        subTotal
        unitPrice
        productVariant {
          id
          name
          sku
          photo {
            name
            url
            thumbUrl
            size
            uid
          }
        }
      }
    }
  `;

  const variables = {
    where: { warehouse: warehouseId, variantIds },
  };
  const data = await client.request(query, variables);
  return data.getVariantLotsByWarehouse as InventoryLotWithVariant[];
};

export const getInventoryLotsByProductAndWarehouse = async (
  productId: string,
  warehouseId: string,
) => {
  const query = gql`
    query ($where: JSON) {
      getILotsByWarehouseAndProduct(where: $where) {
        id
        lotEntryDate
        quantity
        subTotal
        unitPrice
        productVariant {
          id
          name
          sku
        }
      }
    }
  `;

  const variables = {
    where: {
      product: productId,
      warehouse: warehouseId,
    },
  };
  const data = await client.request(query, variables);
  return data.getILotsByWarehouseAndProduct as InventoryLotWithVariant[];
};

export const getInventories = async (params?: InventoriesQueryParams) => {
  const where = {
    query: '',
    filterOptions: undefined as InventoriesQueryParams['filterOptions'],
    warehouse: '',
    type: params && params.productType ? params.productType : undefined,
  };
  if (params?.searchString) {
    where.query = params.searchString;
  }
  if (params?.warehouse) {
    where.warehouse = params.warehouse;
  }
  if (params?.filterOptions) {
    where.filterOptions = params.filterOptions;
  }

  const query = `
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      inventoriesCopy(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          productVariant {
            id
            sku
            name
            type
            avatar
            productTags
            productCategories
            purchasePrice
            sellingPrice
          }
          minUnit
          quantity
          unitPrice
          totalStockValue
          ${warehouseQuantitiesSelection(params?.includeWarehouseQuantities)}
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: params && params.start ? params.start : 1,
    limit: params && params.limit ? params.limit : 10,
    sort: params?.sort,
  };
  const data = await client.request(query, variables);
  return data.inventoriesCopy as {
    data: IInventoryLotResponse[];
    page: number;
    size: number;
    total: number;
  };
};

export const getLowStockInventories = async (params?: LowStockInventoriesQueryParams) => {
  const query = `
    query ($start: Int, $limit: Int) {
      lowStockInventoriesCopy(start: $start, limit: $limit) {
        data {
          id
          productVariant {
            id
            sku
            name
            type
            avatar
            productTags
            productCategories
            purchasePrice
            sellingPrice
          }
          minUnit
          warehouseName
          quantity
          unitPrice
          totalStockValue
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    start: params?.start ?? 1,
    limit: params?.limit ?? 10,
  };

  const data = await client.request(query, variables);
  return data.lowStockInventoriesCopy as {
    data: IInventoryLotResponse[];
    page: number;
    size: number;
    total: number;
  };
};

export const updateInventoryCopyMinUnit = async (data: {
  inventoryCopyId: string;
  minUnit: number | null;
}) => {
  const mutation = gql`
    mutation ($input: UpdateInventoryCopyMinUnitInput!) {
      updateInventoryCopyMinUnit(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: {
      data: {
        inventoryCopyId: data.inventoryCopyId,
        minUnit: data.minUnit,
      },
    },
  };

  const response = await client.request(mutation, variables);
  return response.updateInventoryCopyMinUnit as { id: string };
};

export const getProductsOfWarehouse = async (warehouseId: string) => {
  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      inventoryLots(sort: $sort, where: $where, start: $start, limit: $limit) {
        quantity
        product {
          id
          sku
          name
          avgPurchasePrice
          avgSellingPrice
        }
      }
    }
  `;

  interface Data {
    inventoryLots: {
      product: {
        id: string;
        sku: string;
        name: string;
        avgPurchasePrice: number;
        avgSellingPrice: number;
      };
      quantity: number;
    }[];
  }
  const variables = { where: { warehouse: warehouseId }, start: 0, limit: 10 };
  const data: Data = await client.request(query, variables);
  if (!data.inventoryLots.length) {
    return [];
  }

  interface Product {
    id: string;
    sku: string;
    name: string;
    quantity: number;
    avgPurchasePrice: number;
    avgSellingPrice: number;
  }
  const products = data.inventoryLots.map((inventory) => {
    return { ...inventory.product, quantity: inventory.quantity };
  });

  const mergedDuplicateProducts = products.reduce((previousValue, currentValue) => {
    const { name, sku, quantity } = currentValue;
    const existingProduct = previousValue.find((item) => item.sku === sku && item.name === name);
    if (existingProduct) {
      existingProduct.quantity += quantity;
      return previousValue;
    }

    previousValue.push(currentValue);
    return previousValue;
  }, [] as Product[]);

  return mergedDuplicateProducts;
};

export const getInventoryHistories = async (
  productId: string,
  query?: {
    warehouseId?: string;
    startDate?: string;
    endDate?: string;
    name?: string;
    orderType?: string;
  },
) => {
  const graphqlQuery = gql`
    query (
      $productId: String!
      $warehouseId: String
      $startDate: DateTime
      $endDate: DateTime
      $name: String
      $orderType: AllowedOrderType
    ) {
      inventoryHistoryCopy(
        productId: $productId
        warehouseId: $warehouseId
        startDate: $startDate
        endDate: $endDate
        name: $name
        orderType: $orderType
      ) {
        data {
          orderId
          orderType
          date
          name
          warehouse
          quantity
          unitPrice
          amount
          balanceStock
        }
        keywords
      }
    }
  `;

  const variables = {
    productId,
    warehouseId: query?.warehouseId,
    startDate: query?.startDate,
    endDate: query?.endDate,
    name: query?.name,
    orderType: query?.orderType,
  };
  const data = await client.request(graphqlQuery, variables);
  return data.inventoryHistoryCopy as {
    data: {
      orderId: string;
      orderType: string;
      date: Date;
      name: string;
      warehouse: string;
      quantity: number;
      unitPrice: number;
      amount: number;
      balanceStock: number;
    }[];
    keywords: string;
  };
};

export const getInventoriesDropdown = async (
  warehouseId?: string,
  sort?: ISort | null,
  filterOptions?: InventoriesQueryParams['filterOptions'],
  searchString?: string,
  productType?: InventoriesQueryParams['productType'],
  includeWarehouseQuantities?: boolean,
) => {
  const where = {
    query: '',
    type: productType,
    filterOptions: undefined as InventoriesQueryParams['filterOptions'],
  };
  if (searchString) {
    where.query = searchString;
  }
  if (filterOptions) {
    where.filterOptions = filterOptions;
  }

  const query = gql`
    query InventoryDropdown($where: JSON, $warehouseId: ID, $sort: SortInput) {
      inventoryDropdownCopy(where: $where, warehouseId: $warehouseId, sort: $sort) {
        productVariant {
          id
          sku
          name
          productTags
          productCategories
          purchasePrice
          sellingPrice
        }
        quantity
        unitPrice
        totalStockValue
        ${warehouseQuantitiesSelection(includeWarehouseQuantities)}
      }
    }
  `;

  const variables = { warehouseId, sort, where };
  const data = await client.request(query, variables);
  return data.inventoryDropdownCopy as IInventoryLotResponse[];
};

export const calculateInventoryHistory = async (productId: string, warehouseId?: string) => {
  const query = gql`
    query ($input: calculateInventoryTotalDto!) {
      calculateInventoryHistoryCopy(input: $input)
    }
  `;

  const variables = { input: { productId, warehouseId } };
  const data = await client.request(query, variables);
  return Number(data.calculateInventoryHistoryCopy as string);
};

export const getVariantInventoriesByWarehouse = async (warehouseId: string) => {
  const query = gql`
    query ($where: JSON) {
      getVariantInventoriesByWarehouse(where: $where) {
        id
        quantity
        unitPrice
        totalStockValue
        productVariant {
          id
          name
          sku
          photo {
            name
            url
            thumbUrl
            size
            uid
          }
          purchasePrice
          sellingPrice
        }
      }
    }
  `;

  const variables = {
    where: { warehouse: warehouseId },
  };
  const data = await client.request(query, variables);
  return data.getVariantInventoriesByWarehouse as {
    id: string;
    quantity: number;
    unitPrice: number;
    totalStockValue: number;
    productVariant: {
      id: string;
      name: string;
      sku: string;
      photo?: IAvatar;
      purchasePrice: number;
      sellingPrice: number;
    };
  }[];
};

export const getPOSVariantInventoriesByWarehouse = async ({
  warehouseId,
  categoryId,
  searchText,
}: {
  warehouseId: string;
  categoryId?: string | null;
  searchText?: string | null;
}) => {
  const query = gql`
    query ($where: JSON) {
      getPOSVariantInventoriesByWarehouse(where: $where) {
        id
        sku
        name
        url
        description
        quantity
        unitPrice
      }
    }
  `;

  const variables = {
    where: { warehouse: warehouseId, categoryId, searchText },
  };
  const data = await client.request(query, variables);
  return data.getPOSVariantInventoriesByWarehouse as {
    id: string;
    sku: string;
    name: string;
    url: string;
    description: string;
    quantity: number;
    unitPrice: number;
  }[];
};

// POS (Stitches S5, flag inventory.allShowroomColumns): stock in every showroom for the variants
// a cashier is looking at. Call it only while the flag is on; older APIs lack the query.
export const getVariantStockByWarehouse = async (productVariantIds: string[]) => {
  const query = gql`
    query ($productVariantIds: [ID!]!) {
      variantStockByWarehouse(productVariantIds: $productVariantIds) {
        productVariantId
        warehouseQuantities {
          warehouseId
          warehouseName
          quantity
        }
      }
    }
  `;
  const data = await client.request(query, { productVariantIds });
  return data.variantStockByWarehouse as {
    productVariantId: string;
    warehouseQuantities: WarehouseQuantity[];
  }[];
};

export const getRestaurantPosInventoryByWarehouse = async ({
  warehouseId,
  categoryId,
  searchString,
}: {
  warehouseId: string;
  categoryId?: string | null;
  searchString?: string | null;
}) => {
  const query = gql`
    query ($where: JSON) {
      getRestaurantPOSVariantInventoriesByWarehouse(where: $where) {
        id
        sku
        name
        url
        description
        quantity
        unitPrice
      }
    }
  `;

  const variables = {
    where: { warehouse: warehouseId, categoryId, searchString },
  };
  const data = await client.request(query, variables);
  return data.getRestaurantPOSVariantInventoriesByWarehouse as {
    id: string;
    sku: string;
    name: string;
    url: string;
    description: string;
    quantity: number;
    unitPrice: number;
  }[];
};

export const syncSingleProductInventory = async ({
  productId,
  warehouseId,
}: {
  productId: string;
  warehouseId: string;
}) => {
  const query = gql`
    query ($input: selectedProductInventoryCopyInput!) {
      selectedProductInventoryCopySync(input: $input)
    }
  `;

  const variables = {
    input: { data: [{ productId, warehouseId }] },
  };

  const data = await client.request(query, variables);
  return data.selectedProductInventoryCopySync as string;
};
